import { CloseRounded } from '@mui/icons-material';
import { useBrandNames } from '../../hooks/useBrandNames';
import { describeFilters, type FilterChip } from '../../lib/describeFilters';
import { FILTER_GROUPS } from '../../lib/filters';
import type { AudienceFilters } from '../../types';
import {
  ChipRow,
  ClearChipButton,
  FilterChipBox,
  GroupLabel,
  Groups
} from './FilterSummary.styles';

type FilterSummaryProps = {
  filters: AudienceFilters;
  /** `grouped` labels each group on its own row; `strip` is one wrapping line of chips. */
  layout?: 'grouped' | 'strip';
  /** When set, each chip with a clear patch gets an x that applies it. */
  onClear?: (patch: Partial<AudienceFilters>) => void;
};

/**
 * The filters as chips.
 *
 * The purchase-window chip always shows and has no clear button.
 * Brand chips show brand names from `useBrandNames`, and the raw id for a brand with no known name.
 */
const FilterSummary = ({ filters, layout = 'grouped', onClear }: FilterSummaryProps) => {
  const brandName = useBrandNames([...filters.includedBrandIds, ...filters.excludedBrandIds]);
  const chips = describeFilters(filters, { brandLabel: brandId => brandName(brandId) ?? brandId });

  if (layout === 'strip')
    return (
      <ChipRow>
        {chips.map(chip => (
          <Chip key={chip.id} chip={chip} onClear={onClear} />
        ))}
      </ChipRow>
    );

  return (
    <Groups>
      {FILTER_GROUPS.map(group => {
        const groupChips = chips.filter(chip => chip.group === group.id);
        if (groupChips.length === 0) return null;
        return [
          <GroupLabel key={`${group.id}-label`}>{group.label}</GroupLabel>,
          <ChipRow key={`${group.id}-chips`}>
            {groupChips.map(chip => (
              <Chip key={chip.id} chip={chip} onClear={onClear} />
            ))}
          </ChipRow>
        ];
      })}
    </Groups>
  );
};

const Chip = ({ chip, onClear }: { chip: FilterChip; onClear?: FilterSummaryProps['onClear'] }) => {
  const { clearPatch } = chip;
  return (
    <FilterChipBox tone={chip.tone}>
      {chip.label}
      {onClear && clearPatch ? (
        <ClearChipButton
          type="button"
          aria-label={`Clear ${chip.label}`}
          onClick={() => onClear(clearPatch)}
        >
          <CloseRounded />
        </ClearChipButton>
      ) : null}
    </FilterChipBox>
  );
};

export default FilterSummary;
